import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  KAMIKAZE_ANNOUNCE_DURATION_MS,
  KAMIKAZE_TARGET_IMAGE_PATH,
  KAMIKAZE_SOUND_PATH,
} from '../config/kamikazeAnnouncement';
import {
  SHIELD_UNIT_TARGET_IMAGE_PATH,
  SHIELD_UNIT_TARGET_FADE_DURATION_MS,
} from '../config/shieldUnitStrategemAnimation';
import {
  FLESHSACK_TARGET_IMAGE_PATH,
  FLESHSACK_SOUND_PATH,
  FLESHSACK_TARGET_FADE_DURATION_MS,
} from '../config/fleshsackStrategemAnimation';
import {
  VENOM_CLOUD_TARGET_IMAGE_PATH,
  VENOM_CLOUD_SOUND_PATH,
  VENOM_CLOUD_TARGET_FADE_DURATION_MS,
  VENOM_CLOUD_TARGET_DISSIPATE_DURATION_MS,
  VENOM_CLOUD_TARGET_DISSIPATE_SCALE,
} from '../config/venomCloudStrategemAnimation';
import {
  DUPLICATE_TARGET_IMAGE_PATH,
  DUPLICATE_TARGET_IMAGE_STYLE,
  DUPLICATE_SOUND_PATH,
  DUPLICATE_TARGET_FADE_DURATION_MS,
} from '../config/duplicateStrategemAnimation';
import {
  BETRAYAL_TARGET_IMAGE_PATH,
  BETRAYAL_SOUND_PATH,
  BETRAYAL_TARGET_FADE_DURATION_MS,
  BETRAYAL_TARGET_OVERLAY_WIDTH,
  BETRAYAL_TARGET_ANCHOR_OFFSET_X_PX,
  BETRAYAL_TARGET_ANCHOR_OFFSET_Y_PX,
  BETRAYAL_DRONE_CELL_FLASH_CLASS,
} from '../config/betrayalStrategemAnimation';
import {
  ROT_STRATEGEM_TARGET_IMAGE_PATH,
  ROT_STRATEGEM_SOUND_PATH,
  ROT_STRATEGEM_TARGET_FADE_DURATION_MS,
  ROT_STRATEGEM_TARGET_OVERLAY_WIDTH,
} from '../config/rotStrategemAnimation';
import {
  UNETHICAL_TARGET_IMAGE_PATH,
  UNETHICAL_SOUND_PATH,
  UNETHICAL_TARGET_FADE_DURATION_MS,
  UNETHICAL_TARGET_OVERLAY_MAX_PX,
} from '../config/unethicalStrategemAnimation';
import './betrayalStrategemOverlay.css';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { FRACTURE_MINDS_STRATEGEM_ID } from '../utils/fractureMindsOutcome';
import {
  usesCasterGeneralProjectileOrigin,
  getStrategemCasterGeneralPixelPosition,
} from '../config/casterGeneralStrategemProjectile';
import {
  invalidStrategemTargetPixel,
  isUnusableStrategemTargetPixel,
} from '../utils/strategemTargetPosition';

const WHISPERS_OF_KEK_STRATEGEM_ID = 228;
const EMBALM_STRATEGEM_ID = 226;

/**
 * Map victim general → HUD slot labels used by getTargetPixelPosition (`your` → lower player_general,
 * `opponent` → upper opponent_general).
 * Server `slotPlayerId` for generals is caster-relative; the string `opponent` also matches the DOM
 * role name `opponent_general`, so naïve lookup pins Venom on the upper avatar for the victim unless
 * we resolve by absolute victim id or (318 / 1v1) caster vs viewer.
 */
function resolveHudGeneralPlayerIdForGeneralTarget(
  viewerUserId,
  targetGeneralPlayerId,
  slotPlayerId,
  strategemCasterPlayerId,
  strategemIdNum
) {
  if (viewerUserId != null && targetGeneralPlayerId != null && String(targetGeneralPlayerId) !== '') {
    return String(targetGeneralPlayerId) === String(viewerUserId) ? 'your' : 'opponent';
  }
  // Venom Cloud (318): enemy general only — in 1v1 the hit general belongs to whoever is not the caster.
  if (
    strategemIdNum === 318 &&
    viewerUserId != null &&
    strategemCasterPlayerId != null &&
    String(strategemCasterPlayerId) !== ''
  ) {
    const viewerIsCaster = String(strategemCasterPlayerId) === String(viewerUserId);
    return viewerIsCaster ? 'opponent' : 'your';
  }
  return slotPlayerId;
}

/** Drone rows use opponent_drone / your_drone only (DOM lookup, same role as deployed-unit slot lookup). */
function normalizeDroneZone(zone, playerId) {
  if (zone === 'opponent_drone' || zone === 'your_drone') return zone;
  if (playerId === 'opponent') return 'opponent_drone';
  if (playerId === 'your' || playerId === 'friendly') return 'your_drone';
  return null;
}

function queryDroneSlotElement(zone, index, playerId, instanceIdFallback) {
  if (typeof document === 'undefined') return null;
  const normalizedDroneZone = normalizeDroneZone(zone, playerId);
  const idx =
    index === null || index === undefined || Number.isNaN(Number(index))
      ? null
      : String(Number(index));

  if (!normalizedDroneZone) {
    if (playerId != null && playerId !== '' && idx != null) {
      return document.querySelector(
        `[data-drone-index="${idx}"][data-drone-owner="${playerId}"]`
      );
    }
    return null;
  }

  if (instanceIdFallback != null && String(instanceIdFallback) !== '') {
    const sid = String(instanceIdFallback);
    const byInst = document.querySelector(
      `[data-drone-zone="${normalizedDroneZone}"][data-instance-id="${sid}"]`
    );
    if (byInst) return byInst;
  }

  if (idx != null) {
    const byIdx = document.querySelector(
      `[data-drone-zone="${normalizedDroneZone}"][data-drone-index="${idx}"]`
    );
    if (byIdx) return byIdx;
  }

  const opponentShell =
    document.querySelector('.opponent-drone-zone') ||
    document.querySelector('.opponent-drone-zone-mobile');
  const yourShell =
    document.querySelector('[data-player-drone-zone="true"]') ||
    document.querySelector('.player-drone-zone-mobile');

  const shell =
    normalizedDroneZone === 'opponent_drone' ? opponentShell : yourShell;

  if (shell && instanceIdFallback != null && String(instanceIdFallback) !== '') {
    const sid = String(instanceIdFallback);
    const inner = shell.querySelector(
      `[data-drone-zone="${normalizedDroneZone}"][data-instance-id="${sid}"]`
    );
    if (inner) return inner;
  }
  if (shell && idx != null) {
    const inner = shell.querySelector(
      `[data-drone-zone="${normalizedDroneZone}"][data-drone-index="${idx}"]`
    );
    if (inner) return inner;
  }

  if (playerId != null && playerId !== '' && idx != null) {
    const legacy = document.querySelector(
      `[data-drone-index="${idx}"][data-drone-owner="${playerId}"]`
    );
    if (legacy) return legacy;
  }
  return null;
}

// Missile Barrage (316): number of small glowing yellow dots
const MISSILE_BARRAGE_DOT_COUNT = 8;

// Fracture Minds (59): silver/blue shard swarm (replaces default black orb)
const FRACTURE_MINDS_SHARD_COUNT = 18;

// Cubic Bezier: B(t) = (1-t)³P0 + 3(1-t)²t P1 + 3(1-t)t² P2 + t³ P3
const cubicBezier = (t, p0, p1, p2, p3) => {
  const u = 1 - t;
  const u2 = u * u, u3 = u2 * u;
  const t2 = t * t, t3 = t2 * t;
  return u3 * p0 + 3 * u2 * t * p1 + 3 * u * t2 * p2 + t3 * p3;
};

// Perpendicular offset from line (start->end) for curved "detour" paths
const perpendicularOffset = (start, end, distance) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  const perpX = (-dy / len) * distance;
  const perpY = (dx / len) * distance;
  return { perpX, perpY };
};

/**
 * StrategemTargetAnimation - Ball projectile animation from strategem card in hand to target
 * Black ball for negative effects (damage), yellow ball for positive effects (healing)
 * Strategem 316 (Missile Barrage): many small glowing yellow dots in random chaotic trajectories
 * Strategem 321 (Precision Strike): single precision_missile.png image, nose always pointing at target
 * Strategem 317 (Guided Missile): single guided_missile.png image, nose always pointing at target
 * Strategem 116 (Reaction Shot): single dagger.png image, top always pointing at target (same math as Guided Missile)
 * Strategem 181 (Tactical Breach): same dagger flight from caster general avatar to target (viewer POV: player_general vs opponent_general)
 * Strategem 368 (Damage Pulse): single dmg_pulse.png image, top always pointing at target (same math as Guided Missile)
 * Strategem 54 (Repurposed Warhead): single repurposed_warhead.png image, nose always pointing at target
 * Strategem 228 (Whispers of Kek): kek_atom.png from caster general (same asset as Kek); flies to unit slot on board
 * Strategem 226 (Embalm): embalm.png from caster general; flies to target unit slot on board
 * Strategem 320 (Shield unit): shield_unit_target.png fade-in on target unit (no flying orb)
 * Strategem 324 (Forbidden Clone Protocol): fleshsack_target.png fade-in on target unit (no flying orb)
 * Strategem 318 (Venom Cloud): venom_cloud_target.png fade-in then dissipate on enemy general (no flying orb)
 * Strategem 313 (Duplicate): duplicate_target.png fade-in on friendly unit (no flying orb)
 * Strategem 341 (The Betrayal): betrayal_target.png on unit/drone — bottom-center of art on target (not Duplicate-style center)
 * Strategem 60 (Spread the Rot): rot_target.png on units only — same overlay behavior as Betrayal units; no drones.
 * Strategem 315 (Unethical Experiment): unethical_target.png fade-in on sacrificed friendly unit (no flying orb)
 * Strategem 110, 51, 53, 56, 58, 61, 62, 111, 182, 184, 185, 188, 220, 186: no flying orb (custom VFX on resolve; 186 = Phase Traverse)
 * Strategem 59 (Fracture Minds): swarm of silver/blue shards (no black orb)
 * @param {Object} props
 * @param {Object} props.fromHand - { instanceId, element, position } - Source stratagem card
 * @param {Object} props.toSlot - { zone, index, playerId } - Target slot
 * @param {string} props.targetType - 'unit', 'zone', 'drone', or 'general'
 * @param {string} props.effectType - 'damage' (black) or 'heal' (yellow) - defaults to 'damage'
 * @param {number} props.strategemId - Strategem order ID (e.g. 316 for Missile Barrage)
 * @param {boolean} props.isOpponentStrategem - Whether this is an opponent/bot strategem (should start from opponent general)
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {number} props.animationSpeedMultiplier - Speed multiplier (1 = normal, 0.5 = 2x slower)
 * @param {number} [props.stormMissileSeq] - Storm of Missiles (112): launch stagger index from server
 * @param {string|number} [props.viewerUserId] - POV player id (human / spectated / autobattler bot) for general HUD anchoring
 * @param {string|number} [props.targetGeneralPlayerId] - Absolute game player id of the targeted general (server + local targeting)
 * @param {string|number} [props.strategemCasterPlayerId] - Who played the strategem (for Venom 318 HUD fallback when victim id missing)
 */
export const StrategemTargetAnimation = ({
  fromHand,
  toSlot,
  targetType,
  effectType = 'damage',
  strategemId,
  isOpponentStrategem = false,
  onComplete,
  onDamageAtTarget,
  /** Called when flight is aborted because the target slot/card is not on the board */
  onFlightSkipped,
  animationSpeedMultiplier = 1,
  /** Registered unit target — used to anchor FX on the visible card, not just the slot wrapper */
  targetInstanceId,
  stormMissileSeq = 0,
  viewerUserId,
  targetGeneralPlayerId,
  strategemCasterPlayerId,
}) => {
  const strategemIdNum =
    strategemId === '' || strategemId == null || Number.isNaN(Number(strategemId))
      ? NaN
      : Number(strategemId);
  const isMissileBarrage = strategemIdNum === 316;
  /** Storm of Missiles (112): one curved precision-missile flight per target (general + each enemy unit) */
  const isStormOfMissiles = strategemIdNum === 112;
  const isFractureMinds = strategemIdNum === FRACTURE_MINDS_STRATEGEM_ID;
  const isPrecisionStrike = strategemIdNum === 321;
  const isGuidedMissile = strategemIdNum === 317;
  const isReactionShot = strategemIdNum === 116;
  /** Tactical Breach (181): dagger.png from caster avatar — share motion + rotation with Reaction Shot */
  const isTacticalBreach = strategemIdNum === 181;
  const usesDaggerProjectile = isReactionShot || isTacticalBreach;
  const isDamagePulse = strategemIdNum === 368;
  const isRepurposedWarhead = strategemIdNum === 54;
  const isWhispersOfKek = strategemIdNum === WHISPERS_OF_KEK_STRATEGEM_ID;
  const isEmbalm = strategemIdNum === EMBALM_STRATEGEM_ID;
  const isKamikazeUnitTargeting = strategemIdNum === 314 && targetType === 'unit';
  const isShieldUnitStrategemTargeting = strategemIdNum === 320 && targetType === 'unit';
  const isFleshsackStrategemTargeting = strategemIdNum === 324 && targetType === 'unit';
  const isVenomCloudStrategemTargeting = strategemIdNum === 318 && targetType === 'general';
  const isDuplicateStrategemTargeting = strategemIdNum === 313 && targetType === 'unit';
  const isBetrayalStrategemTargeting =
    strategemIdNum === 341 && (targetType === 'unit' || targetType === 'drone');
  const isRotStrategemTargeting = strategemIdNum === 60 && targetType === 'unit';
  const isUnethicalStrategemTargeting = strategemIdNum === 315 && targetType === 'unit';
  console.log('🎬🎬🎬 StrategemTargetAnimation RENDER', { 
    fromHand, 
    toSlot, 
    targetType,
    strategemId,
    isMissileBarrage,
    'fromHand.position': fromHand?.position 
  });
  
  // Initialize position from fromHand.position immediately so we render right away
  // Whispers of Kek (228) / Embalm (226): start at caster general portrait (not hand card)
  const initialPosition =
    usesCasterGeneralProjectileOrigin(strategemIdNum)
      ? getStrategemCasterGeneralPixelPosition(viewerUserId, strategemCasterPlayerId, isOpponentStrategem) ||
        fromHand?.position ||
        { x: window.innerWidth / 2, y: window.innerHeight - 100 }
      : fromHand?.position || { x: window.innerWidth / 2, y: window.innerHeight - 100 };
  console.log('🎬 Initial position:', initialPosition);
  const [position, setPosition] = useState(initialPosition);
  const [opacity, setOpacity] = useState(1);
  const [isVisible, setIsVisible] = useState(true);
  // Missile Barrage: positions of each dot [{ x, y }, ...]
  const [missileBarrageDots, setMissileBarrageDots] = useState([]);
  // Fracture Minds (59): shard positions + rotation (deg) for silver/blue swarm
  const [fractureMindsShards, setFractureMindsShards] = useState([]);
  // Precision Strike (321): rotation in degrees so missile nose points at target (updated each frame)
  const [precisionMissileRotationDeg, setPrecisionMissileRotationDeg] = useState(0);
  // Guided Missile (317): rotation in degrees so missile nose points at target (updated each frame)
  const [guidedMissileRotationDeg, setGuidedMissileRotationDeg] = useState(0);
  // Reaction Shot (116): dagger.png, top points at target (same convention as Guided Missile)
  const [reactionShotDaggerRotationDeg, setReactionShotDaggerRotationDeg] = useState(0);
  // Damage Pulse (368): dmg_pulse.png, top points at target (same convention as Guided Missile)
  const [damagePulseRotationDeg, setDamagePulseRotationDeg] = useState(0);
  // Repurposed Warhead (54): rotation in degrees so warhead nose points at target (updated each frame)
  const [repurposedWarheadRotationDeg, setRepurposedWarheadRotationDeg] = useState(0);
  const animationRef = useRef(null);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const missileBarrageConfigRef = useRef(null); // { dots: [{ start, end, ctrl1, ctrl2, startDelay, duration }], toPixel }
  const fractureMindsSwarmConfigRef = useRef(null); // { dots (incl shardW/H), toPixel, prevPositions }
  const storm112CurveRef = useRef(null); // { start, end, ctrl1, ctrl2, duration, seqDelay, toPixel }
  const storm112PrevPtRef = useRef({ x: 0, y: 0 });
  const [storm112Missile, setStorm112Missile] = useState(null); // { x, y, rotDeg }
  const kamikazeFlightStartedRef = useRef(false);
  const [kamikazeAnnounceOverlay, setKamikazeAnnounceOverlay] = useState(false);
  const [kamikazeAnnouncePos, setKamikazeAnnouncePos] = useState(null);
  const [kamikazeAnnounceOpacity, setKamikazeAnnounceOpacity] = useState(0);
  const kamikazeAnnounceTimersRef = useRef({ announce: null, fly: null });
  const shield320FlightStartedRef = useRef(false);
  const [shieldUnitOverlay, setShieldUnitOverlay] = useState(false);
  const [shieldUnitPos, setShieldUnitPos] = useState(null);
  const [shieldUnitOpacity, setShieldUnitOpacity] = useState(0);
  const shieldUnitTimersRef = useRef({ announce: null, complete: null });
  const fleshsack324FlightStartedRef = useRef(false);
  const [fleshsackOverlay, setFleshsackOverlay] = useState(false);
  const [fleshsackPos, setFleshsackPos] = useState(null);
  const [fleshsackOpacity, setFleshsackOpacity] = useState(0);
  const fleshsackTimersRef = useRef({ announce: null, complete: null });
  const venomCloud318FlightStartedRef = useRef(false);
  const [venomCloudOverlay, setVenomCloudOverlay] = useState(false);
  const [venomCloudPos, setVenomCloudPos] = useState(null);
  const [venomCloudOpacity, setVenomCloudOpacity] = useState(0);
  const [venomCloudScale, setVenomCloudScale] = useState(1);
  const [venomCloudDissipating, setVenomCloudDissipating] = useState(false);
  const venomCloudTimersRef = useRef({ announce: null, dissipate: null, complete: null });
  const duplicate313FlightStartedRef = useRef(false);
  const [duplicateOverlay, setDuplicateOverlay] = useState(false);
  const [duplicatePos, setDuplicatePos] = useState(null);
  const [duplicateOpacity, setDuplicateOpacity] = useState(0);
  const duplicateTimersRef = useRef({ announce: null, complete: null });
  const betrayal341FlightStartedRef = useRef(false);
  const [betrayalOverlay, setBetrayalOverlay] = useState(false);
  const [betrayalPos, setBetrayalPos] = useState(null);
  const [betrayalOpacity, setBetrayalOpacity] = useState(0);
  const betrayalTimersRef = useRef({ announce: null, complete: null });
  const betrayalDroneFlashElRef = useRef(null);
  const rot60FlightStartedRef = useRef(false);
  const [rotOverlay, setRotOverlay] = useState(false);
  const [rotPos, setRotPos] = useState(null);
  const [rotOpacity, setRotOpacity] = useState(0);
  const rotTimersRef = useRef({ announce: null, complete: null });
  const unethical315FlightStartedRef = useRef(false);
  const [unethicalOverlay, setUnethicalOverlay] = useState(false);
  const [unethicalPos, setUnethicalPos] = useState(null);
  const [unethicalOpacity, setUnethicalOpacity] = useState(0);
  const unethicalTimersRef = useRef({ announce: null, complete: null });

  // Hardcoded slot coordinates (cell-based) - from DeployTargetAnimation
  // Grid format: "cell X,Y" where X is horizontal, Y is vertical
  // Grid uses 20px cell size (from GridOverlay.js)
  const slotCoordinates = {
    // Opponent Front (slots 1-5)
    'opponent_front_0': { cellX: 4, cellY: 10 },   // Slot 1
    'opponent_front_1': { cellX: 14, cellY: 10 }, // Slot 2
    'opponent_front_2': { cellX: 24, cellY: 9 },  // Slot 3
    'opponent_front_3': { cellX: 32, cellY: 10 }, // Slot 4
    'opponent_front_4': { cellX: 42, cellY: 9 },  // Slot 5
    
    // Opponent Flank (slots 6-10)
    'opponent_flank_0': { cellX: 52, cellY: 10 }, // Slot 6
    'opponent_flank_1': { cellX: 52, cellY: 11 }, // Slot 7
    'opponent_flank_2': { cellX: 70, cellY: 10 }, // Slot 8
    'opponent_flank_3': { cellX: 79, cellY: 10 }, // Slot 9
    'opponent_flank_4': { cellX: 86, cellY: 10 }, // Slot 10
    
    // Your Front (slots 11-15)
    'your_front_0': { cellX: 3, cellY: 18 },   // Slot 11
    'your_front_1': { cellX: 15, cellY: 18 }, // Slot 12
    'your_front_2': { cellX: 22, cellY: 18 }, // Slot 13
    'your_front_3': { cellX: 32, cellY: 17 }, // Slot 14
    'your_front_4': { cellX: 39, cellY: 18 }, // Slot 15
    
    // Your Flank (slots 16-20)
    'your_flank_0': { cellX: 52, cellY: 18 }, // Slot 16
    'your_flank_1': { cellX: 62, cellY: 18 }, // Slot 17
    'your_flank_2': { cellX: 69, cellY: 17 }, // Slot 18
    'your_flank_3': { cellX: 81, cellY: 18 }, // Slot 19
    'your_flank_4': { cellX: 89, cellY: 17 }, // Slot 20
    
    // Drone zone coordinates (approximate center of drone slots)
    'opponent_drone_0': { cellX: 10, cellY: 6 },
    'opponent_drone_1': { cellX: 25, cellY: 6 },
    'opponent_drone_2': { cellX: 40, cellY: 6 },
    'opponent_drone_3': { cellX: 55, cellY: 6 },
    'your_drone_0': { cellX: 10, cellY: 22 },
    'your_drone_1': { cellX: 25, cellY: 22 },
    'your_drone_2': { cellX: 40, cellY: 22 },
    'your_drone_3': { cellX: 55, cellY: 22 },
  };

  // Zone center coordinates for zone-targeting stratagems
  const zoneCenterCoordinates = {
    'opponent_front': { cellX: 24, cellY: 10 },
    'opponent_flank': { cellX: 70, cellY: 10 },
    'your_front': { cellX: 22, cellY: 18 },
    'your_flank': { cellX: 70, cellY: 18 },
    'front': { cellX: 22, cellY: 18 }, // Alias for your_front
    'flank': { cellX: 70, cellY: 18 }, // Alias for your_flank
  };

  // Convert cell coordinates to pixel coordinates
  const GRID_CELL_SIZE = 20;
  const GRID_OFFSET_X = 0;
  const GRID_OFFSET_Y = 0;

  const cellToPixel = (cellX, cellY) => {
    return {
      x: GRID_OFFSET_X + (cellX * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2),
      y: GRID_OFFSET_Y + (cellY * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2)
    };
  };

  /** Bounding rect for deployed unit card art (same element chain as center/bottom anchors). */
  const getDeployedUnitArtRect = (normalizedZone, index, instanceIdFallback) => {
    if (typeof document === 'undefined') return null;
    const idxStr = String(index);
    // Prefer the card surface node directly so horizontal center matches the 200px tile (not an outer
    // flex wrapper that also carries data-slot-* and can be wider than the card on some rows).
    const directCard =
      document.querySelector(
        `.deployed-unit-slot[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"] .order-design-card-bounds`
      ) ||
      document.querySelector(
        `[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"] .order-design-card-bounds`
      );
    if (directCard) {
      const r = directCard.getBoundingClientRect();
      if (r.width >= 2 && r.height >= 2) return r;
    }
    let slotEl = document.querySelector(
      `.deployed-unit-slot[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"]`
    );
    if (!slotEl) {
      slotEl = document.querySelector(
        `[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"]`
      );
    }
    if (!slotEl && instanceIdFallback != null && instanceIdFallback !== '') {
      const suffix = `_${String(instanceIdFallback)}`;
      const nodes = document.querySelectorAll('[data-unit-key]');
      for (let i = 0; i < nodes.length; i++) {
        const key = nodes[i].getAttribute('data-unit-key') || '';
        if (key.endsWith(suffix)) {
          slotEl = nodes[i];
          break;
        }
      }
    }
    if (!slotEl) return null;
    const artEl =
      slotEl.querySelector('.order-design-minimal') ||
      slotEl.querySelector('.order-design-card-bounds');
    const src = artEl || slotEl;
    const rect = src.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return null;
    return rect;
  };

  /** Center of the visible minimal order card — matches where the player aims (not the outer flex wrapper). */
  const getDeployedUnitArtCenterPixels = (normalizedZone, index, instanceIdFallback) => {
    const rect = getDeployedUnitArtRect(normalizedZone, index, instanceIdFallback);
    if (!rect) return null;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  };

  /** Betrayal (341): anchor = bottom-center of card rect so translate(-50%,-100%) sits art on the unit. Drones use a tile flash instead — no overlay anchor. */
  const getBetrayalAnchorPixels = (zone, index, playerId) => {
    const ox = BETRAYAL_TARGET_ANCHOR_OFFSET_X_PX;
    const oy = BETRAYAL_TARGET_ANCHOR_OFFSET_Y_PX;
    const bottomCenter = (rect) => ({
      x: rect.left + rect.width / 2 + ox,
      y: rect.bottom + oy,
    });

    if (!zone) {
      return { x: window.innerWidth / 2 + ox, y: window.innerHeight / 2 + oy };
    }

    let normalizedZone = zone;
    if (zone === 'front' || zone === 'flank') {
      const playerPrefix = playerId === 'opponent' ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    } else if (!zone.includes('_')) {
      const playerPrefix = playerId === 'opponent' ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    }

    const artRect = getDeployedUnitArtRect(normalizedZone, index, targetInstanceId);
    if (artRect) {
      return bottomCenter(artRect);
    }

    const idxStr = String(index);
    let slotElement = document.querySelector(
      `.deployed-unit-slot[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"]`
    );
    if (!slotElement) {
      slotElement = document.querySelector(
        `[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"]`
      );
    }
    if (slotElement) {
      return bottomCenter(slotElement.getBoundingClientRect());
    }

    const slotKey = `${normalizedZone}_${index}`;
    const coords = slotCoordinates[slotKey];
    if (coords) {
      const p = cellToPixel(coords.cellX, coords.cellY);
      return { x: p.x + ox, y: p.y + GRID_CELL_SIZE / 2 + oy };
    }

    const zoneSlots = document.querySelectorAll(`[data-slot-zone="${normalizedZone}"]`);
    if (zoneSlots.length > 0) {
      return bottomCenter(zoneSlots[0].getBoundingClientRect());
    }

    const zoneCenterKey = normalizedZone
      .replace('_0', '')
      .replace('_1', '')
      .replace('_2', '')
      .replace('_3', '')
      .replace('_4', '');
    const zoneCenterCoords = zoneCenterCoordinates[zoneCenterKey] || zoneCenterCoordinates[normalizedZone];
    if (zoneCenterCoords) {
      const p = cellToPixel(zoneCenterCoords.cellX, zoneCenterCoords.cellY);
      return { x: p.x + ox, y: p.y + GRID_CELL_SIZE / 2 + oy };
    }

    return { x: window.innerWidth / 2 + ox, y: window.innerHeight / 2 + oy };
  };

  const getTargetPixelPosition = (zone, index, playerId, targetTypeParam) => {
    // Handle general targets - use DOM lookup
    if (zone === 'general' || targetTypeParam === 'general') {
      const hudGeneralPlayerId = resolveHudGeneralPlayerIdForGeneralTarget(
        viewerUserId,
        targetGeneralPlayerId,
        playerId,
        strategemCasterPlayerId,
        strategemIdNum
      );
      let generalElement = null;
      
      // DOM lookup for general element
      
      if (hudGeneralPlayerId === 'your' || hudGeneralPlayerId === 'friendly') {
        generalElement = document.querySelector(`[data-zone="player_general"]`);
      } else if (hudGeneralPlayerId === 'opponent') {
        generalElement = document.querySelector(`[data-zone="opponent_general"]`);
      } else {
        // Fallback: try both if playerId is not recognized
        generalElement = document.querySelector(`[data-zone="player_general"]`) || 
                         document.querySelector(`[data-zone="opponent_general"]`);
      }
      
      if (generalElement) {
        const rect = generalElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
      
      // Fallback positions
      if (hudGeneralPlayerId === 'your' || hudGeneralPlayerId === 'friendly') {
        return { x: 200, y: 600 };
      } else {
        return { x: 1600, y: 100 };
      }
    }

    // Handle zone targeting (entire zone, not specific unit)
    if (targetTypeParam === 'zone' && (index === null || index === undefined)) {
      // ✅ FIX: Normalize zone for zone targeting too
      let zoneKey = zone;
      if (zone === 'front' || zone === 'flank') {
        const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
        zoneKey = `${playerPrefix}_${zone}`;
      } else if (!zone.includes('_')) {
        const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
        zoneKey = `${playerPrefix}_${zone}`;
      }
      
      const coords = zoneCenterCoordinates[zoneKey];
      if (coords) {
        return cellToPixel(coords.cellX, coords.cellY);
      }
      console.warn(`⚠️ StrategemTargetAnimation: No zone center coordinates for ${zoneKey} (original: ${zone})`);
      return { x: 400, y: 300 }; // Fallback center
    }

    // Handle drone targeting — DOM tile center for damage / FX (mirrors deployed unit handling).
    if (targetTypeParam === 'drone' || zone === 'opponent_drone' || zone === 'your_drone') {
      const droneKey = `${zone}_${index}`;
      const droneElement = queryDroneSlotElement(zone, index, playerId, targetInstanceId);
      if (droneElement) {
        const rect = droneElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      }
      const coords = slotCoordinates[droneKey];
      if (coords) {
        return cellToPixel(coords.cellX, coords.cellY);
      }
      console.warn(`⚠️ StrategemTargetAnimation: No coordinates for drone ${droneKey}`);
      return { x: 200, y: 200 };
    }
    
    // Handle unit targeting (specific slot)
    // If zone is undefined/null, can't look up position
    if (!zone) {
      console.warn(`⚠️ StrategemTargetAnimation: zone is undefined, returning fallback position`);
      return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }
    
    // ✅ FIX: Normalize zone to always include player prefix for consistent DOM lookup
    // This ensures we always use the correct zone name that matches the DOM attributes
    let normalizedZone = zone;
    if (zone === 'front' || zone === 'flank') {
      // Zone without prefix - add player prefix based on playerId
      const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    } else if (!zone.includes('_')) {
      // Zone without prefix and not 'front'/'flank' - still add prefix
      const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
      normalizedZone = `${playerPrefix}_${zone}`;
    }
    
    // Build the correct slot key using normalized zone and playerId
    let slotKey = `${normalizedZone}_${index}`;
    
    // Use DOM lookup for accurate positioning
    // ✅ ALWAYS use DOM lookup for accurate positioning
    // The hardcoded cell coordinates don't match actual screen positions
    console.log(`🎬 StrategemTargetAnimation: Looking for slot with zone="${normalizedZone}", index=${index}, original zone="${zone}", playerId="${playerId}"`);
    const artCenter = getDeployedUnitArtCenterPixels(
      normalizedZone,
      index,
      targetInstanceId
    );
    if (artCenter) {
      console.log(
        `✅ StrategemTargetAnimation: Anchored to order card art center at (${artCenter.x}, ${artCenter.y})`
      );
      return artCenter;
    }

    const idxStr = String(index);
    const slotElement = document.querySelector(
      `[data-slot-zone="${normalizedZone}"][data-slot-index="${idxStr}"]`
    );

    if (slotElement) {
      const artInSlot =
        slotElement.querySelector('.order-design-card-bounds') ||
        slotElement.querySelector('.order-design-minimal');
      // Phantom-guard: refuse empty slots with no registered target.
      // Bot/opponent kill path: unit is often already removed from state; targetInstanceId means
      // this animation is for a real hit — aim at the slot center so flight + damage still play.
      if (targetTypeParam === 'unit' && !artInSlot && targetInstanceId == null) {
        return invalidStrategemTargetPixel(
          `empty slot ${normalizedZone}[${index}]`
        );
      }
      const rect = (artInSlot || slotElement).getBoundingClientRect();
      const pixelPos = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      console.log(`✅ StrategemTargetAnimation: Found slot element at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }

    if (targetTypeParam === 'unit' && targetInstanceId == null) {
      return invalidStrategemTargetPixel(
        `no slot for ${normalizedZone}[${index}]`
      );
    }

    if (targetTypeParam === 'unit' && targetInstanceId != null) {
      // Last resort for resolved kills: slot DOM missing (layout) but we still have a known target.
      const coords = slotCoordinates[slotKey];
      if (coords) {
        const pixelPos = cellToPixel(coords.cellX, coords.cellY);
        console.warn(
          `⚠️ StrategemTargetAnimation: Empty/missing slot for kill target ${targetInstanceId}; using hardcoded coords (${pixelPos.x}, ${pixelPos.y})`
        );
        return pixelPos;
      }
      return invalidStrategemTargetPixel(
        `no slot for ${normalizedZone}[${index}] (instanceId ${targetInstanceId})`
      );
    }

    // Zone / non-unit: hardcoded slot or zone center (never first arbitrary slot in row)
    console.warn(
      `⚠️ StrategemTargetAnimation: DOM lookup failed for zone="${normalizedZone}", index=${index}, using hardcoded coords for slotKey="${slotKey}"`
    );
    const coords = slotCoordinates[slotKey];
    if (coords) {
      const pixelPos = cellToPixel(coords.cellX, coords.cellY);
      console.log(`⚠️ StrategemTargetAnimation: Using hardcoded coords at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }

    const zoneCenterKey = normalizedZone
      .replace('_0', '')
      .replace('_1', '')
      .replace('_2', '')
      .replace('_3', '')
      .replace('_4', '');
    const zoneCenterCoords = zoneCenterCoordinates[zoneCenterKey] || zoneCenterCoordinates[normalizedZone];
    if (zoneCenterCoords) {
      const pixelPos = cellToPixel(zoneCenterCoords.cellX, zoneCenterCoords.cellY);
      console.log(`⚠️ StrategemTargetAnimation: Using zone center as fallback at (${pixelPos.x}, ${pixelPos.y})`);
      return pixelPos;
    }

    console.error(
      `❌ StrategemTargetAnimation: All fallbacks failed for zone="${normalizedZone}", index=${index}, using screen center`
    );
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  };

  const getHandCardPosition = () => {
    if (usesCasterGeneralProjectileOrigin(strategemIdNum)) {
      const fromGeneral = getStrategemCasterGeneralPixelPosition(
        viewerUserId,
        strategemCasterPlayerId,
        isOpponentStrategem
      );
      if (fromGeneral) return fromGeneral;
    }

    // ✅ FIX: For opponent/bot strategems, always use the provided position or opponent general position
    // Don't try to find the card in DOM (it's in the opponent's hand, not visible)
    if (isOpponentStrategem) {
      // Use pre-calculated position if available (should be opponent general position)
      if (fromHand?.position) {
        console.log('🎬 StrategemTargetAnimation: Using provided position for opponent strategem:', fromHand.position);
        return fromHand.position;
      }
      
      // Fallback: Get opponent general position directly
      const opponentGeneralElement = document.querySelector('[data-zone="opponent_general"]');
      if (opponentGeneralElement) {
        const rect = opponentGeneralElement.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        console.log('🎬 StrategemTargetAnimation: Using opponent general position for bot strategem:', position);
        return position;
      }
      
      // Final fallback for opponent strategems
      console.warn('⚠️ StrategemTargetAnimation: Could not find opponent general, using fallback');
      return { x: window.innerWidth - 200, y: 100 };
    }
    
    // For player strategems: Use pre-calculated position if available (captured before card removal)
    if (fromHand?.position) {
      return fromHand.position;
    }
    
    // Try to find the stratagem card in hand by instanceId
    if (fromHand?.instanceId) {
      const cardElement = document.querySelector(`[data-instance-id="${fromHand.instanceId}"]`);
      if (cardElement) {
        const rect = cardElement.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
    }
    
    // Fallback: approximate hand position (center-bottom of screen)
    console.warn('⚠️ StrategemTargetAnimation: Could not find hand card position, using fallback');
    return { x: window.innerWidth / 2, y: window.innerHeight - 100 };
  };

  useEffect(() => {
    console.log('🎬 StrategemTargetAnimation: Component mounted/updated', { fromHand, toSlot, targetType, isAnimating: isAnimatingRef.current });
    
    if (!fromHand || !toSlot || isAnimatingRef.current) {
      console.log('⚠️ StrategemTargetAnimation: Skipping - missing props or already animating', { fromHand, toSlot, isAnimating: isAnimatingRef.current });
      return;
    }

    console.log(`🎬 StrategemTargetAnimation: Starting animation`, { fromHand, toSlot, targetType });
    
    // Small delay to ensure DOM is ready (especially for hand card lookup)
    const finishKamikazeUnitAfterAnnounce = (toPixel) => {
      kamikazeFlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const finishShieldUnitAfterFade = (toPixel) => {
      shield320FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const finishFleshsackAfterFade = (toPixel) => {
      fleshsack324FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const finishVenomCloudAfterFade = (toPixel) => {
      venomCloud318FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const finishDuplicateAfterFade = (toPixel) => {
      duplicate313FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const finishBetrayalAfterFade = () => {
      betrayal341FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        // Damage / explosion must use the same center point as Duplicate & flying-orbs (getTargetPixelPosition).
        // Overlay anchor uses getBetrayalAnchorPixels (bottom-center + offsets) — never pass that here or VFX land wrong.
        const damageAt = getTargetPixelPosition(
          toSlot.zone,
          toSlot.index,
          toSlot.playerId,
          targetType
        );
        onDamageAtTarget({
          x: damageAt.x,
          y: damageAt.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType,
        });
      }
      if (onComplete) onComplete();
    };

    const finishRotAfterFade = () => {
      rot60FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        const damageAt = getTargetPixelPosition(
          toSlot.zone,
          toSlot.index,
          toSlot.playerId,
          targetType
        );
        onDamageAtTarget({
          x: damageAt.x,
          y: damageAt.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType,
        });
      }
      if (onComplete) onComplete();
    };

    const finishUnethicalAfterFade = (toPixel) => {
      unethical315FlightStartedRef.current = true;
      isAnimatingRef.current = false;
      setIsVisible(false);
      if (onDamageAtTarget) {
        onDamageAtTarget({
          x: toPixel.x,
          y: toPixel.y,
          zone: toSlot.zone,
          index: toSlot.index,
          playerId: toSlot.playerId,
          targetType: targetType
        });
      }
      if (onComplete) onComplete();
    };

    const startFlight = () => {
      const fromPixel = getHandCardPosition();
      const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
      
      if (isUnusableStrategemTargetPixel(toPixel)) {
        console.warn(
          `⏭️ StrategemTargetAnimation: Skipping flight — target not on board (${toSlot.zone}[${toSlot.index}]${targetInstanceId ? ` ${targetInstanceId}` : ''})`
        );
        // Death detection already suppressed this instanceId when the kill resolved; if we skip
        // flight there is no onDamageAtTarget lethal path — release suppression so a late
        // slot-death pass (or a following state tick) is not permanently muted.
        if (typeof onFlightSkipped === 'function') {
          onFlightSkipped({
            targetInstanceId,
            zone: toSlot?.zone,
            index: toSlot?.index,
            strategemId,
          });
        }
        if (onComplete) onComplete();
        return;
      }
          
      console.log(`🎬 StrategemTargetAnimation: Starting animation from hand to ${toSlot.zone}${toSlot.index !== null && toSlot.index !== undefined ? `[${toSlot.index}]` : ''}`);
      console.log(`🎬 Pixel coordinates: from (${fromPixel.x}, ${fromPixel.y}) to (${toPixel.x}, ${toPixel.y})`);
      
      isAnimatingRef.current = true;
      startTimeRef.current = Date.now();
      const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
      const DURATION = Math.round(300 / speedFactor); // 300ms at 100% speed

      if (isStormOfMissiles) {
        const spread = 22;
        const start = {
          x: fromPixel.x + (Math.random() - 0.5) * spread,
          y: fromPixel.y + (Math.random() - 0.5) * spread
        };
        const end = {
          x: toPixel.x + (Math.random() - 0.5) * (spread * 0.45),
          y: toPixel.y + (Math.random() - 0.5) * (spread * 0.45)
        };
        const { perpX, perpY } = perpendicularOffset(start, end, 1);
        const detour = 85 + Math.random() * 105;
        const side = Math.random() > 0.5 ? 1 : -1;
        const oneThirdX = start.x + (end.x - start.x) * 0.33;
        const oneThirdY = start.y + (end.y - start.y) * 0.33;
        const twoThirdX = start.x + (end.x - start.x) * 0.67;
        const twoThirdY = start.y + (end.y - start.y) * 0.67;
        const ctrl1 = {
          x: oneThirdX + perpX * detour * side,
          y: oneThirdY + perpY * detour * side
        };
        const ctrl2 = {
          x: twoThirdX + perpX * (detour * 0.78) * -side,
          y: twoThirdY + perpY * (detour * 0.78) * -side
        };
        const seqDelay = (stormMissileSeq || 0) * 55;
        const duration = Math.round((430 / speedFactor) * (0.92 + Math.random() * 0.14));
        storm112CurveRef.current = {
          start,
          end,
          ctrl1,
          ctrl2,
          duration,
          seqDelay,
          toPixel
        };
        storm112PrevPtRef.current = { ...start };
        const dx0 = end.x - start.x;
        const dy0 = end.y - start.y;
        const rot0 = (Math.atan2(dy0, dx0) * 180) / Math.PI + 90;
        setStorm112Missile({ x: start.x, y: start.y, rotDeg: Number.isFinite(rot0) ? rot0 : 0 });
        startTimeRef.current = Date.now();

        const animateStorm112 = () => {
          const cfg = storm112CurveRef.current;
          if (!cfg) return;
          const elapsedRaw = Date.now() - startTimeRef.current;
          const elapsed = elapsedRaw - cfg.seqDelay;
          if (elapsed < 0) {
            animationRef.current = requestAnimationFrame(animateStorm112);
            return;
          }
          const t = Math.min(elapsed / cfg.duration, 1);
          const eased =
            t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const x = cubicBezier(eased, cfg.start.x, cfg.ctrl1.x, cfg.ctrl2.x, cfg.end.x);
          const y = cubicBezier(eased, cfg.start.y, cfg.ctrl1.y, cfg.ctrl2.y, cfg.end.y);
          const prev = storm112PrevPtRef.current;
          let rotDeg = (Math.atan2(y - prev.y, x - prev.x) * 180) / Math.PI + 90;
          if (!Number.isFinite(rotDeg)) rotDeg = 0;
          storm112PrevPtRef.current = { x, y };
          setStorm112Missile({ x, y, rotDeg });
          if (t >= 1) {
            isAnimatingRef.current = false;
            animationRef.current = null;
            storm112CurveRef.current = null;
            setStorm112Missile(null);
            setIsVisible(false);
            if (onDamageAtTarget) {
              onDamageAtTarget({
                x: cfg.toPixel.x,
                y: cfg.toPixel.y,
                zone: toSlot.zone,
                index: toSlot.index,
                playerId: toSlot.playerId,
                targetType
              });
            }
            if (onComplete) onComplete();
            return;
          }
          animationRef.current = requestAnimationFrame(animateStorm112);
        };
        animationRef.current = requestAnimationFrame(animateStorm112);
        return;
      }

      if (isMissileBarrage) {
        // Missile Barrage (316): fewer dots, each taking a curved detour (not straight line)
        const spread = 24;
        const dots = [];
        for (let i = 0; i < MISSILE_BARRAGE_DOT_COUNT; i++) {
          const start = {
            x: fromPixel.x + (Math.random() - 0.5) * spread * 2,
            y: fromPixel.y + (Math.random() - 0.5) * spread * 2
          };
          const end = {
            x: toPixel.x + (Math.random() - 0.5) * spread,
            y: toPixel.y + (Math.random() - 0.5) * spread
          };
          // Control points push path perpendicular to the line so dots arc away then back (less optimal route)
          const { perpX, perpY } = perpendicularOffset(start, end, 1);
          const detour = 70 + Math.random() * 90; // 70–160px perpendicular detour
          const side = Math.random() > 0.5 ? 1 : -1;
          const oneThirdX = start.x + (end.x - start.x) * 0.33;
          const oneThirdY = start.y + (end.y - start.y) * 0.33;
          const twoThirdX = start.x + (end.x - start.x) * 0.67;
          const twoThirdY = start.y + (end.y - start.y) * 0.67;
          const ctrl1 = {
            x: oneThirdX + perpX * detour * side,
            y: oneThirdY + perpY * detour * side
          };
          const ctrl2 = {
            x: twoThirdX + perpX * (detour * 0.8) * -side,
            y: twoThirdY + perpY * (detour * 0.8) * -side
          };
          dots.push({
            start,
            end,
            ctrl1,
            ctrl2,
            startDelay: Math.random() * 60,
            duration: DURATION * (0.85 + Math.random() * 0.5)
          });
        }
        missileBarrageConfigRef.current = { dots, toPixel };
        setMissileBarrageDots(dots.map(d => ({ x: d.start.x, y: d.start.y })));

        const animateMissileBarrage = () => {
          const elapsed = Date.now() - startTimeRef.current;
          const config = missileBarrageConfigRef.current;
          if (!config) return;
          const { dots: dotConfigs, toPixel: toPx } = config;
          const newPositions = dotConfigs.map((d, i) => {
            const t = Math.max(0, (elapsed - d.startDelay) / d.duration);
            const progress = Math.min(t, 1);
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            const x = cubicBezier(eased, d.start.x, d.ctrl1.x, d.ctrl2.x, d.end.x);
            const y = cubicBezier(eased, d.start.y, d.ctrl1.y, d.ctrl2.y, d.end.y);
            return { x, y };
          });
          setMissileBarrageDots(newPositions);
          const allDone = dotConfigs.every((d, i) => {
            const t = (elapsed - d.startDelay) / d.duration;
            return t >= 1;
          });
          if (allDone) {
            isAnimatingRef.current = false;
            animationRef.current = null;
            missileBarrageConfigRef.current = null;
            setIsVisible(false);
            if (onDamageAtTarget) {
              onDamageAtTarget({
                x: toPx.x,
                y: toPx.y,
                zone: toSlot.zone,
                index: toSlot.index,
                playerId: toSlot.playerId,
                targetType: targetType
              });
            }
            if (onComplete) onComplete();
          } else {
            animationRef.current = requestAnimationFrame(animateMissileBarrage);
          }
        };
        animationRef.current = requestAnimationFrame(animateMissileBarrage);
        return;
      }

      if (isFractureMinds) {
        const spread = 32;
        const dots = [];
        for (let i = 0; i < FRACTURE_MINDS_SHARD_COUNT; i++) {
          const start = {
            x: fromPixel.x + (Math.random() - 0.5) * spread * 2,
            y: fromPixel.y + (Math.random() - 0.5) * spread * 2
          };
          const end = {
            x: toPixel.x + (Math.random() - 0.5) * spread,
            y: toPixel.y + (Math.random() - 0.5) * spread
          };
          const { perpX, perpY } = perpendicularOffset(start, end, 1);
          const detour = 85 + Math.random() * 110;
          const side = Math.random() > 0.5 ? 1 : -1;
          const oneThirdX = start.x + (end.x - start.x) * 0.33;
          const oneThirdY = start.y + (end.y - start.y) * 0.33;
          const twoThirdX = start.x + (end.x - start.x) * 0.67;
          const twoThirdY = start.y + (end.y - start.y) * 0.67;
          const ctrl1 = {
            x: oneThirdX + perpX * detour * side,
            y: oneThirdY + perpY * detour * side
          };
          const ctrl2 = {
            x: twoThirdX + perpX * (detour * 0.75) * -side,
            y: twoThirdY + perpY * (detour * 0.75) * -side
          };
          dots.push({
            start,
            end,
            ctrl1,
            ctrl2,
            startDelay: Math.random() * 80,
            duration: DURATION * (0.75 + Math.random() * 0.55),
            shardW: 4 + Math.random() * 4,
            shardH: 11 + Math.random() * 14
          });
        }
        const prevPositions = dots.map(d => ({ x: d.start.x, y: d.start.y }));
        fractureMindsSwarmConfigRef.current = { dots, toPixel, prevPositions };
        setFractureMindsShards(
          dots.map(d => ({
            x: d.start.x,
            y: d.start.y,
            rotDeg: Math.random() * 360,
            shardW: d.shardW,
            shardH: d.shardH
          }))
        );

        const animateFractureMindsSwarm = () => {
          const elapsed = Date.now() - startTimeRef.current;
          const config = fractureMindsSwarmConfigRef.current;
          if (!config) return;
          const { dots: dotConfigs, toPixel: toPx } = config;
          const newShards = dotConfigs.map((d, i) => {
            const t = Math.max(0, (elapsed - d.startDelay) / d.duration);
            const progress = Math.min(t, 1);
            const eased = progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            const x = cubicBezier(eased, d.start.x, d.ctrl1.x, d.ctrl2.x, d.end.x);
            const y = cubicBezier(eased, d.start.y, d.ctrl1.y, d.ctrl2.y, d.end.y);
            const prev = config.prevPositions[i];
            let rotDeg = (Math.atan2(y - prev.y, x - prev.x) * 180) / Math.PI;
            if (!Number.isFinite(rotDeg)) rotDeg = 0;
            config.prevPositions[i] = { x, y };
            return { x, y, rotDeg, shardW: d.shardW, shardH: d.shardH };
          });
          setFractureMindsShards(newShards);
          const allDone = dotConfigs.every(d => {
            const t = (elapsed - d.startDelay) / d.duration;
            return t >= 1;
          });
          if (allDone) {
            isAnimatingRef.current = false;
            animationRef.current = null;
            fractureMindsSwarmConfigRef.current = null;
            setIsVisible(false);
            if (onDamageAtTarget) {
              onDamageAtTarget({
                x: toPx.x,
                y: toPx.y,
                zone: toSlot.zone,
                index: toSlot.index,
                playerId: toSlot.playerId,
                targetType: targetType
              });
            }
            if (onComplete) onComplete();
          } else {
            animationRef.current = requestAnimationFrame(animateFractureMindsSwarm);
          }
        };
        animationRef.current = requestAnimationFrame(animateFractureMindsSwarm);
        return;
      }

      setPosition(fromPixel);
      setOpacity(1);
      if (isPrecisionStrike) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setPrecisionMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isGuidedMissile) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setGuidedMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (usesDaggerProjectile) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setReactionShotDaggerRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isDamagePulse) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setDamagePulseRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      if (isRepurposedWarhead) {
        const dx = toPixel.x - fromPixel.x;
        const dy = toPixel.y - fromPixel.y;
        const angleRad = Math.atan2(dy, dx);
        setRepurposedWarheadRotationDeg((angleRad * 180 / Math.PI) + 90);
      }
      
      const animate = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / DURATION, 1);
        
        // Ease-in-out easing
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        // Interpolate position
        const currentX = fromPixel.x + (toPixel.x - fromPixel.x) * eased;
        const currentY = fromPixel.y + (toPixel.y - fromPixel.y) * eased;
        
        setPosition({ x: currentX, y: currentY });
        // Precision Strike / Guided Missile: keep missile nose pointing at target (image top = -90deg in CSS)
        if (isPrecisionStrike) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setPrecisionMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isGuidedMissile) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setGuidedMissileRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (usesDaggerProjectile) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setReactionShotDaggerRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isDamagePulse) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setDamagePulseRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        if (isRepurposedWarhead) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setRepurposedWarheadRotationDeg((angleRad * 180 / Math.PI) + 90);
        }
        
        if (progress < 1) {
          const rafId = requestAnimationFrame(animate);
          animationRef.current = rafId;
        } else {
          // Animation complete - trigger damage animation at target position
          console.log(`✅ StrategemTargetAnimation: Animation complete at (${toPixel.x}, ${toPixel.y})`);
          isAnimatingRef.current = false;
          animationRef.current = null;
          setIsVisible(false);
          
          // Trigger damage animation at the exact target position
          if (onDamageAtTarget) {
            onDamageAtTarget({
              x: toPixel.x,
              y: toPixel.y,
              zone: toSlot.zone,
              index: toSlot.index,
              playerId: toSlot.playerId,
              targetType: targetType
            });
          }
          
          if (onComplete) onComplete();
        }
      };
      
      const rafId = requestAnimationFrame(animate);
      animationRef.current = rafId;
    };

    if (strategemIdNum === 318 && targetType === 'general') {
      isAnimatingRef.current = true;
      venomCloudTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Venom Cloud (318) failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setVenomCloudPos(toPixel);
        setVenomCloudOpacity(0);
        setVenomCloudScale(1);
        setVenomCloudDissipating(false);
        setVenomCloudOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setVenomCloudOpacity(1));
        });
        playSoundEffect(VENOM_CLOUD_SOUND_PATH);
        venomCloudTimersRef.current.dissipate = setTimeout(() => {
          setVenomCloudDissipating(true);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setVenomCloudOpacity(0);
              setVenomCloudScale(VENOM_CLOUD_TARGET_DISSIPATE_SCALE);
            });
          });
          venomCloudTimersRef.current.complete = setTimeout(() => {
            setVenomCloudOverlay(false);
            setVenomCloudDissipating(false);
            setVenomCloudScale(1);
            finishVenomCloudAfterFade(toPixel);
          }, VENOM_CLOUD_TARGET_DISSIPATE_DURATION_MS);
        }, VENOM_CLOUD_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (venomCloudTimersRef.current.announce) {
          clearTimeout(venomCloudTimersRef.current.announce);
          venomCloudTimersRef.current.announce = null;
        }
        if (venomCloudTimersRef.current.dissipate) {
          clearTimeout(venomCloudTimersRef.current.dissipate);
          venomCloudTimersRef.current.dissipate = null;
        }
        if (venomCloudTimersRef.current.complete) {
          clearTimeout(venomCloudTimersRef.current.complete);
          venomCloudTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // Do not call onComplete here: React 18 Strict Mode runs this cleanup between double-mounts and
        // would remove the animation from the parent before overlay timers fire (blank betrayal / duplicate).
      };
    }

    if (strategemIdNum === 313 && targetType === 'unit') {
      isAnimatingRef.current = true;
      duplicateTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Duplicate (313) failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setDuplicatePos(toPixel);
        setDuplicateOpacity(0);
        setDuplicateOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setDuplicateOpacity(1));
        });
        playSoundEffect(DUPLICATE_SOUND_PATH);
        duplicateTimersRef.current.complete = setTimeout(() => {
          setDuplicateOverlay(false);
          finishDuplicateAfterFade(toPixel);
        }, DUPLICATE_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (duplicateTimersRef.current.announce) {
          clearTimeout(duplicateTimersRef.current.announce);
          duplicateTimersRef.current.announce = null;
        }
        if (duplicateTimersRef.current.complete) {
          clearTimeout(duplicateTimersRef.current.complete);
          duplicateTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // Do not call onComplete here — same Strict Mode issue as Venom (318).
      };
    }

    if (strategemIdNum === 341 && targetType === 'drone') {
      isAnimatingRef.current = true;
      const clearBetrayalDroneFlash = () => {
        const el = betrayalDroneFlashElRef.current;
        if (el && el.isConnected) {
          el.classList.remove(BETRAYAL_DRONE_CELL_FLASH_CLASS);
          el.style.animationDuration = '';
        }
        betrayalDroneFlashElRef.current = null;
      };

      betrayalTimersRef.current.announce = setTimeout(() => {
        const droneEl = queryDroneSlotElement(
          toSlot.zone,
          toSlot.index,
          toSlot.playerId,
          targetInstanceId
        );
        if (droneEl) {
          clearBetrayalDroneFlash();
          betrayalDroneFlashElRef.current = droneEl;
          droneEl.style.animationDuration = `${BETRAYAL_TARGET_FADE_DURATION_MS}ms`;
          droneEl.classList.add(BETRAYAL_DRONE_CELL_FLASH_CLASS);
        } else {
          console.error(
            'StrategemTargetAnimation: Betrayal (341) could not find drone slot element for flash'
          );
        }
        playSoundEffect(BETRAYAL_SOUND_PATH);
        betrayalTimersRef.current.complete = setTimeout(() => {
          clearBetrayalDroneFlash();
          finishBetrayalAfterFade();
        }, BETRAYAL_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (betrayalTimersRef.current.announce) {
          clearTimeout(betrayalTimersRef.current.announce);
          betrayalTimersRef.current.announce = null;
        }
        if (betrayalTimersRef.current.complete) {
          clearTimeout(betrayalTimersRef.current.complete);
          betrayalTimersRef.current.complete = null;
        }
        clearBetrayalDroneFlash();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
      };
    }

    if (strategemIdNum === 341 && targetType === 'unit') {
      isAnimatingRef.current = true;
      betrayalTimersRef.current.announce = setTimeout(() => {
        const toPixel = getBetrayalAnchorPixels(toSlot.zone, toSlot.index, toSlot.playerId);
        if (
          !toPixel ||
          !Number.isFinite(toPixel.x) ||
          !Number.isFinite(toPixel.y) ||
          isUnusableStrategemTargetPixel(toPixel)
        ) {
          console.error('StrategemTargetAnimation: Betrayal (341) bad overlay anchor, aborting', toPixel);
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setBetrayalPos(toPixel);
        setBetrayalOpacity(0);
        setBetrayalOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setBetrayalOpacity(1));
        });
        playSoundEffect(BETRAYAL_SOUND_PATH);
        betrayalTimersRef.current.complete = setTimeout(() => {
          setBetrayalOverlay(false);
          finishBetrayalAfterFade();
        }, BETRAYAL_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (betrayalTimersRef.current.announce) {
          clearTimeout(betrayalTimersRef.current.announce);
          betrayalTimersRef.current.announce = null;
        }
        if (betrayalTimersRef.current.complete) {
          clearTimeout(betrayalTimersRef.current.complete);
          betrayalTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
      };
    }

    if (strategemIdNum === 60 && targetType === 'unit') {
      isAnimatingRef.current = true;
      rotTimersRef.current.announce = setTimeout(() => {
        const toPixel = getBetrayalAnchorPixels(toSlot.zone, toSlot.index, toSlot.playerId);
        if (
          !toPixel ||
          !Number.isFinite(toPixel.x) ||
          !Number.isFinite(toPixel.y) ||
          isUnusableStrategemTargetPixel(toPixel)
        ) {
          console.error('StrategemTargetAnimation: Spread the Rot (60) bad overlay anchor, aborting', toPixel);
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setRotPos(toPixel);
        setRotOpacity(0);
        setRotOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setRotOpacity(1));
        });
        playSoundEffect(ROT_STRATEGEM_SOUND_PATH);
        rotTimersRef.current.complete = setTimeout(() => {
          setRotOverlay(false);
          finishRotAfterFade();
        }, ROT_STRATEGEM_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (rotTimersRef.current.announce) {
          clearTimeout(rotTimersRef.current.announce);
          rotTimersRef.current.announce = null;
        }
        if (rotTimersRef.current.complete) {
          clearTimeout(rotTimersRef.current.complete);
          rotTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
      };
    }

    if (strategemIdNum === 315 && targetType === 'unit') {
      isAnimatingRef.current = true;
      unethicalTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Unethical Experiment (315) failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setUnethicalPos(toPixel);
        setUnethicalOpacity(0);
        setUnethicalOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setUnethicalOpacity(1));
        });
        playSoundEffect(UNETHICAL_SOUND_PATH);
        unethicalTimersRef.current.complete = setTimeout(() => {
          setUnethicalOverlay(false);
          finishUnethicalAfterFade(toPixel);
        }, UNETHICAL_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (unethicalTimersRef.current.announce) {
          clearTimeout(unethicalTimersRef.current.announce);
          unethicalTimersRef.current.announce = null;
        }
        if (unethicalTimersRef.current.complete) {
          clearTimeout(unethicalTimersRef.current.complete);
          unethicalTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // No onComplete in cleanup (Strict Mode).
      };
    }

    if (strategemIdNum === 324 && targetType === 'unit') {
      isAnimatingRef.current = true;
      fleshsackTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Forbidden Clone Protocol (324) failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setFleshsackPos(toPixel);
        setFleshsackOpacity(0);
        setFleshsackOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setFleshsackOpacity(1));
        });
        playSoundEffect(FLESHSACK_SOUND_PATH);
        fleshsackTimersRef.current.complete = setTimeout(() => {
          setFleshsackOverlay(false);
          finishFleshsackAfterFade(toPixel);
        }, FLESHSACK_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (fleshsackTimersRef.current.announce) {
          clearTimeout(fleshsackTimersRef.current.announce);
          fleshsackTimersRef.current.announce = null;
        }
        if (fleshsackTimersRef.current.complete) {
          clearTimeout(fleshsackTimersRef.current.complete);
          fleshsackTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // No onComplete in cleanup (Strict Mode).
      };
    }

    if (strategemIdNum === 320 && targetType === 'unit') {
      isAnimatingRef.current = true;
      shieldUnitTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Shield unit (320) failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setShieldUnitPos(toPixel);
        setShieldUnitOpacity(0);
        setShieldUnitOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setShieldUnitOpacity(1));
        });
        shieldUnitTimersRef.current.complete = setTimeout(() => {
          setShieldUnitOverlay(false);
          finishShieldUnitAfterFade(toPixel);
        }, SHIELD_UNIT_TARGET_FADE_DURATION_MS);
      }, 50);

      return () => {
        if (shieldUnitTimersRef.current.announce) {
          clearTimeout(shieldUnitTimersRef.current.announce);
          shieldUnitTimersRef.current.announce = null;
        }
        if (shieldUnitTimersRef.current.complete) {
          clearTimeout(shieldUnitTimersRef.current.complete);
          shieldUnitTimersRef.current.complete = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // No onComplete in cleanup (Strict Mode).
      };
    }

    if (strategemIdNum === 314 && targetType === 'unit') {
      isAnimatingRef.current = true;
      kamikazeAnnounceTimersRef.current.announce = setTimeout(() => {
        const toPixel = getTargetPixelPosition(toSlot.zone, toSlot.index, toSlot.playerId, targetType);
        if (isUnusableStrategemTargetPixel(toPixel)) {
          console.error('❌ StrategemTargetAnimation: Kamikaze announce failed target position');
          isAnimatingRef.current = false;
          if (onComplete) onComplete();
          return;
        }
        setKamikazeAnnouncePos(toPixel);
        setKamikazeAnnounceOpacity(0);
        setKamikazeAnnounceOverlay(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setKamikazeAnnounceOpacity(1));
        });
        playSoundEffect(KAMIKAZE_SOUND_PATH);
        kamikazeAnnounceTimersRef.current.fly = setTimeout(() => {
          setKamikazeAnnounceOverlay(false);
          finishKamikazeUnitAfterAnnounce(toPixel);
        }, KAMIKAZE_ANNOUNCE_DURATION_MS);
      }, 50);

      return () => {
        if (kamikazeAnnounceTimersRef.current.announce) {
          clearTimeout(kamikazeAnnounceTimersRef.current.announce);
          kamikazeAnnounceTimersRef.current.announce = null;
        }
        if (kamikazeAnnounceTimersRef.current.fly) {
          clearTimeout(kamikazeAnnounceTimersRef.current.fly);
          kamikazeAnnounceTimersRef.current.fly = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        // No onComplete in cleanup (Strict Mode).
      };
    }

    if (strategemIdNum === 186) {
      // Phase Traverse: unit reposition only — no projectile; finish so two-phase queue / WS completion still run.
      timeoutRef.current = setTimeout(() => {
        if (onComplete) onComplete();
      }, 50);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
    
    // Small delay to ensure DOM is ready
    timeoutRef.current = setTimeout(() => {
      startFlight();
    }, 50);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      isAnimatingRef.current = false;
      // Do not call onComplete here: Strict Mode runs this between double-mounts and would drop the
      // animation before startFlight / overlays run. Normal completion always invokes onComplete from finish handlers or RAF path.
    };
  }, [fromHand?.instanceId, toSlot?.zone, toSlot?.index, toSlot?.playerId, targetType, strategemId, strategemIdNum, targetInstanceId, viewerUserId, targetGeneralPlayerId, strategemCasterPlayerId, isMissileBarrage, isStormOfMissiles, stormMissileSeq, isFractureMinds, isPrecisionStrike, isGuidedMissile, usesDaggerProjectile, isDamagePulse, isRepurposedWarhead, animationSpeedMultiplier]);

  // Ball style - black for damage, yellow for healing (single projectile)
  const getProjectileStyle = () => {
    const isHealing = effectType === 'heal';
    return {
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      transform: 'translate(-50%, -50%)',
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      pointerEvents: 'none',
      zIndex: 10000,
      opacity: opacity,
      transition: 'none',
      backgroundColor: isHealing ? '#ffd700' : 'black', // Yellow for healing, black for damage
      boxShadow: isHealing 
        ? '0 0 10px rgba(255, 215, 0, 0.8)' 
        : '0 0 10px rgba(0, 0, 0, 0.8)',
      border: isHealing 
        ? '2px solid rgba(255, 215, 0, 0.5)' 
        : '2px solid rgba(0, 0, 0, 0.5)'
    };
  };

  if (isVenomCloudStrategemTargeting && !venomCloud318FlightStartedRef.current) {
    if (venomCloudOverlay && venomCloudPos) {
      const dissipateMs = VENOM_CLOUD_TARGET_DISSIPATE_DURATION_MS;
      return (
        <img
          src={VENOM_CLOUD_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            position: 'fixed',
            left: `${venomCloudPos.x}px`,
            top: `${venomCloudPos.y}px`,
            transform: `translate(-50%, -50%) scale(${venomCloudScale})`,
            width: 'min(44vw, 220px)',
            height: 'auto',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: venomCloudOpacity,
            transition: venomCloudDissipating
              ? `opacity ${dissipateMs}ms ease-out, transform ${dissipateMs}ms ease-out, filter ${dissipateMs}ms ease-out`
              : 'opacity 0.35s ease-out',
            filter: venomCloudDissipating
              ? 'blur(10px) drop-shadow(0 0 18px rgba(60, 200, 100, 0.25))'
              : 'drop-shadow(0 0 14px rgba(60, 200, 100, 0.5))',
          }}
        />
      );
    }
    return null;
  }

  if (isDuplicateStrategemTargeting && !duplicate313FlightStartedRef.current) {
    if (duplicateOverlay && duplicatePos) {
      return (
        <img
          src={DUPLICATE_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            ...DUPLICATE_TARGET_IMAGE_STYLE,
            position: 'fixed',
            left: `${duplicatePos.x}px`,
            top: `${duplicatePos.y}px`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: duplicateOpacity,
            transition: 'opacity 0.35s ease-out',
          }}
        />
      );
    }
    return null;
  }

  if (isRotStrategemTargeting && !rot60FlightStartedRef.current) {
    if (rotOverlay && rotPos) {
      const rotLayer = (
        <div
          aria-hidden
          className="strategem-betrayal-overlay-flicker"
          style={{
            position: 'fixed',
            left: `${rotPos.x}px`,
            top: `${rotPos.y}px`,
            transform: 'translate(-50%, -100%)',
            display: 'inline-block',
            lineHeight: 0,
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: rotOpacity,
            transition: 'opacity 0.35s ease-out',
          }}
        >
          <img
            src={ROT_STRATEGEM_TARGET_IMAGE_PATH}
            alt=""
            draggable={false}
            style={{
              display: 'block',
              width: ROT_STRATEGEM_TARGET_OVERLAY_WIDTH,
              height: 'auto',
              objectFit: 'contain',
              margin: 0,
              padding: 0,
              border: 'none',
            }}
          />
        </div>
      );
      if (typeof document !== 'undefined' && document.body) {
        return createPortal(rotLayer, document.body);
      }
      return rotLayer;
    }
    return null;
  }

  if (isBetrayalStrategemTargeting && !betrayal341FlightStartedRef.current) {
    if (targetType === 'drone') {
      return null;
    }
    if (betrayalOverlay && betrayalPos) {
      const betrayalLayer = (
        <div
          aria-hidden
          className="strategem-betrayal-overlay-flicker"
          style={{
            position: 'fixed',
            left: `${betrayalPos.x}px`,
            top: `${betrayalPos.y}px`,
            transform: 'translate(-50%, -100%)',
            display: 'inline-block',
            lineHeight: 0,
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: betrayalOpacity,
            transition: 'opacity 0.35s ease-out',
          }}
        >
          <img
            src={BETRAYAL_TARGET_IMAGE_PATH}
            alt=""
            draggable={false}
            style={{
              display: 'block',
              width: BETRAYAL_TARGET_OVERLAY_WIDTH,
              height: 'auto',
              objectFit: 'contain',
              margin: 0,
              padding: 0,
              border: 'none',
            }}
          />
        </div>
      );
      if (typeof document !== 'undefined' && document.body) {
        return createPortal(betrayalLayer, document.body);
      }
      return betrayalLayer;
    }
    return null;
  }

  if (isUnethicalStrategemTargeting && !unethical315FlightStartedRef.current) {
    if (unethicalOverlay && unethicalPos) {
      return (
        <img
          src={UNETHICAL_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            position: 'fixed',
            left: `${unethicalPos.x}px`,
            top: `${unethicalPos.y}px`,
            transform: 'translate(-50%, -50%)',
            width: UNETHICAL_TARGET_OVERLAY_MAX_PX,
            maxWidth: UNETHICAL_TARGET_OVERLAY_MAX_PX,
            maxHeight: UNETHICAL_TARGET_OVERLAY_MAX_PX,
            height: 'auto',
            objectFit: 'contain',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: unethicalOpacity,
            transition: 'opacity 0.35s ease-out',
            filter: 'drop-shadow(0 0 14px rgba(200, 80, 140, 0.5))',
          }}
        />
      );
    }
    return null;
  }

  if (isFleshsackStrategemTargeting && !fleshsack324FlightStartedRef.current) {
    if (fleshsackOverlay && fleshsackPos) {
      return (
        <img
          src={FLESHSACK_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            position: 'fixed',
            left: `${fleshsackPos.x}px`,
            top: `${fleshsackPos.y}px`,
            transform: 'translate(-50%, -50%)',
            width: 'min(44vw, 220px)',
            height: 'auto',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: fleshsackOpacity,
            transition: 'opacity 0.35s ease-out',
            filter: 'drop-shadow(0 0 14px rgba(160, 90, 120, 0.5))',
          }}
        />
      );
    }
    return null;
  }

  if (isShieldUnitStrategemTargeting && !shield320FlightStartedRef.current) {
    if (shieldUnitOverlay && shieldUnitPos) {
      return (
        <img
          src={SHIELD_UNIT_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            position: 'fixed',
            left: `${shieldUnitPos.x}px`,
            top: `${shieldUnitPos.y}px`,
            transform: 'translate(-50%, -50%)',
            width: 'min(44vw, 220px)',
            height: 'auto',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: shieldUnitOpacity,
            transition: 'opacity 0.35s ease-out',
            filter: 'drop-shadow(0 0 14px rgba(80, 160, 255, 0.45))',
          }}
        />
      );
    }
    return null;
  }

  if (isKamikazeUnitTargeting && !kamikazeFlightStartedRef.current) {
    if (kamikazeAnnounceOverlay && kamikazeAnnouncePos) {
      return (
        <img
          src={KAMIKAZE_TARGET_IMAGE_PATH}
          alt=""
          aria-hidden
          style={{
            position: 'fixed',
            left: `${kamikazeAnnouncePos.x}px`,
            top: `${kamikazeAnnouncePos.y}px`,
            transform: 'translate(-50%, -50%)',
            width: 'min(44vw, 220px)',
            height: 'auto',
            pointerEvents: 'none',
            zIndex: 10002,
            opacity: kamikazeAnnounceOpacity,
            transition: 'opacity 0.35s ease-out',
            filter: 'drop-shadow(0 0 14px rgba(220, 50, 40, 0.5))',
          }}
        />
      );
    }
    return null;
  }

  // Pulse Shockwave (110), Culling (51), Neurotox (53), Viral Echo (56), Plague Bloom (58), Cradle (61), Orbital Riftstrike (184), Hive Surge (62), Gift of the Rift (182), Kinetron Blast (111), Shadow Bargain (185), Essence Siphon (188), Plague of Water turned to Blood (220), Phase Traverse (186): no flying projectile
  if (
    strategemIdNum === 110 ||
    strategemIdNum === 51 ||
    strategemIdNum === 53 ||
    strategemIdNum === 56 ||
    strategemIdNum === 58 ||
    strategemIdNum === 61 ||
    strategemIdNum === 184 ||
    strategemIdNum === 62 ||
    strategemIdNum === 182 ||
    strategemIdNum === 111 ||
    strategemIdNum === 185 ||
    strategemIdNum === 188 ||
    strategemIdNum === 220 ||
    strategemIdNum === 186
  ) {
    return null;
  }

  // Always render if we have valid initial position
  if (!isVisible) {
    return null;
  }

  // Storm of Missiles (112): no default orb — curved missile state is set in startFlight
  if (isStormOfMissiles && storm112Missile == null) {
    return null;
  }

  // Fracture Minds (59): silver/blue shard swarm — hide single orb until swarm initializes
  if (isFractureMinds && fractureMindsShards.length === 0) {
    return null;
  }

  // Fracture Minds (59): glowing silver/blue shards along curved paths
  if (isFractureMinds && fractureMindsShards.length > 0) {
    return (
      <>
        {fractureMindsShards.map((shard, i) => (
          <div
            key={i}
            aria-hidden
            style={{
              position: 'fixed',
              left: `${shard.x}px`,
              top: `${shard.y}px`,
              transform: `translate(-50%, -50%) rotate(${shard.rotDeg}deg)`,
              width: `${shard.shardW ?? 6}px`,
              height: `${shard.shardH ?? 16}px`,
              pointerEvents: 'none',
              zIndex: 10000,
              opacity,
              transition: 'none',
              borderRadius: '1px',
              background:
                'linear-gradient(180deg, rgba(248,252,255,0.98) 0%, rgba(160,210,255,0.92) 38%, rgba(90,150,235,0.88) 72%, rgba(55,105,195,0.85) 100%)',
              boxShadow:
                '0 0 5px rgba(220,240,255,0.95), 0 0 12px rgba(130,190,255,0.75), 0 0 22px rgba(80,140,220,0.45)',
              border: '1px solid rgba(200,230,255,0.55)'
            }}
          />
        ))}
      </>
    );
  }

  // Missile Barrage (316): many small glowing yellow dots
  if (isMissileBarrage && missileBarrageDots.length > 0) {
    return (
      <>
        {missileBarrageDots.map((dot, i) => (
          <div
            key={i}
            style={{
              position: 'fixed',
              left: `${dot.x}px`,
              top: `${dot.y}px`,
              transform: 'translate(-50%, -50%)',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              pointerEvents: 'none',
              zIndex: 10000,
              backgroundColor: '#ffd700',
              boxShadow: '0 0 8px rgba(255, 215, 0, 0.95), 0 0 14px rgba(255, 220, 0, 0.6)',
              border: '1px solid rgba(255, 255, 200, 0.9)'
            }}
          />
        ))}
      </>
    );
  }

  // Storm of Missiles (112): one precision missile per target along a cubic Bezier arc
  if (isStormOfMissiles && storm112Missile) {
    return (
      <img
        src="/images/precision_missile.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${storm112Missile.x}px`,
          top: `${storm112Missile.y}px`,
          transform: `translate(-50%, -50%) rotate(${storm112Missile.rotDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Precision Strike (321): missile image with nose always pointing at target
  if (isPrecisionStrike) {
    return (
      <img
        src="/images/precision_missile.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${precisionMissileRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Guided Missile (317): missile image with nose always pointing at target
  if (isGuidedMissile) {
    return (
      <img
        src="/images/guided_missile.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${guidedMissileRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Reaction Shot (116) & Tactical Breach (181): dagger.png, top always pointing at target (181 starts from caster general portrait)
  if (usesDaggerProjectile) {
    return (
      <img
        src="/images/dagger.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${reactionShotDaggerRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Damage Pulse (368): dmg_pulse.png, top always pointing at target (same motion/rotation as Guided Missile)
  if (isDamagePulse) {
    return (
      <img
        src="/images/dmg_pulse.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${damagePulseRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Repurposed Warhead (54): warhead image with nose always pointing at target (larger than other missiles)
  if (isRepurposedWarhead) {
    return (
      <img
        src="/images/repurposed_warhead.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${repurposedWarheadRotationDeg}deg)`,
          width: '120px',
          height: 'auto',
          maxHeight: '297px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Embalm (226): embalm.png — same flight as Whispers of Kek (caster general → unit slot)
  if (isEmbalm) {
    return (
      <div
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
          width: '40px',
          height: '40px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      >
        <img
          src="/images/embalm.png"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  // Whispers of Kek (228): kek_atom — flight starts at caster general (see getHandCardPosition), ends on unit slot
  if (isWhispersOfKek) {
    return (
      <div
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)',
          width: '40px',
          height: '40px',
          pointerEvents: 'none',
          zIndex: 10000,
          opacity,
          transition: 'none'
        }}
      >
        <img
          src="/images/kek_atom.png"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  return (
    <div style={getProjectileStyle()} />
  );
};

export default StrategemTargetAnimation;
